import { h, Fragment } from 'preact';
import { createPortal } from 'preact/compat';

import { getMaxContents, shouldCollapseWidget } from '../../../utils';
import { getTagSelectName } from '../getters/get-derived-values';
import { getLabel } from '../../../../labels/labelling-service';
import SectionTitle from '../../../components/2-atoms/section-title/section-title';
import TagSelect from '../../../components/2-atoms/tag-select/tag-select';
import Card from '../../../components/3-molecules/card/card';
import TagList from '../../../components/3-molecules/tag-list/tag-list';
import DropdownBasic from '../../../components/3-molecules/dropdown/dropdown-basic';
import DropdownFloating from '../../../components/3-molecules/dropdown/dropdown-floating';
import ModalPrivacy from '../../../components/3-molecules/modal-privacy/modal-privacy';
import SetOfCards from '../../../components/4-organisms/set-of-cards/set-of-cards';
import { ParselyWrapper, ParselyVideoWrapper, ParselyBody, ParselyTop } from '../parsely-wrapper';
import { ctaAppend } from '../parsely';
import { parseParselyPrefsForDropdown } from '../../../utils/parsers';

import { renderParselySkeleton } from './render-parsely-skeleton';
import { renderCta } from './render-cta';

export const renderInRail = ({
  state,
  isWidgetLoading,
  derivedValues,
  widget,
  htmlProps,
  authState,
  parselyCommonState,
  handlers,
  isLoginCardVisible,
  modalPrivacyContainer,
  showModalPrivacy,
}) => {
  if (shouldCollapseWidget(state.error?.status)) {
    return null;
  }

  if (isWidgetLoading) {
    return renderParselySkeleton({
      derivedValues,
      widget,
      htmlProps,
      userHasPreferences: Boolean(state.userPreferences.length),
      cardModifiers: widget.styles.cardModifiers[htmlProps.position],
      maxContents: getMaxContents(htmlProps),
    });
  }

  const showTagSelect = authState.isUserLogged;
  const showTagList = authState.isUserLogged && widget.isTagListActive && state.userPreferences.length > 0;
  const showDropdown = authState.isUserLogged && state.showPreferencesList;

  const tagSelectName = getTagSelectName(state, widget, htmlProps);
  const urlLogo = widget.showLogo ? state.userPreferences?.[0]?.config?.urlLogo : '';
  const isMessageCardVisible =
    Boolean(derivedValues.messageCard) && !parselyCommonState.hideMessage && !parselyCommonState.currentConsent;

  return (
    <Fragment>
      <ParselyWrapper>
        <ParselyTop>
          <SectionTitle modifiers={derivedValues.sectionTitleModifiers}>{derivedValues.title}</SectionTitle>
          {derivedValues.showRemoveConsent && (
            <DropdownFloating
              options={[
                {
                  text: getLabel('parsely.common.privacy.cancel.consent'),
                  onClick: () => handlers.handlePrivacyMutation(false),
                },
              ]}
            />
          )}
        </ParselyTop>
        <ParselyBody>
          <Fragment>
            {!isMessageCardVisible && (
              <Fragment>
                {showTagSelect && (
                  <TagSelect
                    isDefault={state.userPreferences.length === 0}
                    urlLogo={urlLogo}
                    name={tagSelectName}
                    onClick={() => handlers.handlePreferencesListOpen(parselyCommonState.currentConsent)}
                    iconsColor={widget.styles.iconsColor}
                  />
                )}
                {showTagList && (
                  <TagList
                    tags={state.userPreferences.map((tag) => ({
                      name: tag.config.labelCta,
                      url: tag.config.linkCta,
                    }))}
                    targetCta={htmlProps.target}
                    intcmpTag={htmlProps.intcmpCta}
                  />
                )}
                {showDropdown && (
                  <DropdownBasic
                    isOpen
                    onHeaderClick={handlers.handlePreferencesListClose}
                    onCancel={handlers.handlePreferencesListClose}
                    onConfirm={handlers.handlePreferencesListConfirm}
                    options={parseParselyPrefsForDropdown(state.widgetPreferences)}
                    selectedOptions={parseParselyPrefsForDropdown(state.selectedOptions)}
                    title={widget.tagSelectDefaultTextOpened}
                    editTitle={widget.tagSelectEditText}
                    messageSelect={widget.preferencesList.messageSelect}
                    sectionTitle={derivedValues.title}
                    sectionTitleModifiers={derivedValues.sectionTitleModifiers}
                    iconsColor={widget.styles.iconsColor}
                    maxOptions={htmlProps.maxOptions}
                    onOptionClick={handlers.handleOptionClick}
                  />
                )}
              </Fragment>
            )}
            <SetOfCards
              cards={state.contents}
              modifiers={derivedValues.setOfCarsModifierList}
              isLoginCardVisible={isLoginCardVisible}
              isMessageCardVisible={isMessageCardVisible}
              iconsColor={widget.styles.iconsColor}
              iconsColorSecondary={widget.styles.iconsColorSecondary}
              htmlId={widget.htmlId}
              loginCardText={widget.loginCardText}
              messageCard={derivedValues.messageCard}
              onLoginCardClose={handlers.handleLoginCardClose}
              onMessageCardClose={handlers.handleMessageCardClose}
              render={(card) => (
                <Card
                  key={card.pubDate}
                  modifiers={widget.styles.cardModifiers[htmlProps.position]}
                  newsData={card}
                  intcmpCard={htmlProps.intcmpArticle}
                  target={htmlProps.target}
                  isParsely
                />
              )}
            />
            {state.videoContents.length === 0 &&
              widget.isCtaActive &&
              renderCta({
                isUserLogged: authState.isUserLogged,
                userPreferences: state.userPreferences,
                ctaAppend,
                intcmpCta: htmlProps.intcmpCta,
                ctaDefaultUrl: htmlProps.ctaDefaultUrl,
                ctaDefaultLabel: htmlProps.ctaDefaultLabel,
              })}
          </Fragment>
        </ParselyBody>
      </ParselyWrapper>
      {state.videoContents.length > 0 && (
        <ParselyVideoWrapper>
          <SectionTitle modifiers={['no-margin-top']}>{getLabel('global.video')}</SectionTitle>
          <SetOfCards
            cards={state.videoContents}
            modifiers={derivedValues.setOfCarsModifierList}
            render={(card) => (
              <Card
                key={card.pubDate}
                modifiers={widget.styles.cardModifiers[htmlProps.position]}
                newsData={card}
                intcmpVideo={htmlProps.intcmpVideo}
                target={htmlProps.target}
                imageAspectRatio="16-9"
                isParsely
              />
            )}
          />
          {widget.isCtaActive &&
            renderCta({
              isUserLogged: authState.isUserLogged,
              userPreferences: state.userPreferences,
              ctaAppend,
              intcmpCta: htmlProps.intcmpCta,
              ctaDefaultUrl: htmlProps.ctaDefaultUrl,
              ctaDefaultLabel: htmlProps.ctaDefaultLabel,
            })}
        </ParselyVideoWrapper>
      )}
      {showModalPrivacy &&
        createPortal(
          <ModalPrivacy
            isActive
            isLoading={state.isMutationPrivacyPending}
            htmlId={widget.htmlId}
            htmlProps={htmlProps}
            onCancel={handlers.handleModalPrivacyCancel}
            onConfirm={() => handlers.handlePrivacyMutation(true)}
          />,
          modalPrivacyContainer
        )}
    </Fragment>
  );
};
